import React, { useMemo, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, LabelList, Cell, ReferenceLine,
} from "recharts";
import { DEFAULT_BRAND_STATES, defaultSeriesKeys, brandFamilyColor } from "./Brandparse";

const T = {
  surface: "#FFFFFF", bgAlt: "#FFF8F8", border: "#FADDDD", borderMed: "#F5BABA",
  accent: "#E8001A", accentSoft: "#FFF0F0", ink: "#12100E",
  text: "#1A0005", textMid: "#5C1020", textDim: "#9A5060",
  pos: "#1A7A3A", neg: "#C42020",
  shadow: "0 2px 8px rgba(232,0,26,0.08)",
};

const MAX_STATES = 6;

const fmtVpo = (v) => (v == null || isNaN(v) ? "" : Number(v).toFixed(1));
const fmtWd  = (v) => (v == null || isNaN(v) ? "" : `${(v * 100).toFixed(1)}%`);

/* KO brands read as "ours", everything else as competition */
const isKO = (b) => /sprite|fanta|coke|limca|thums|tux|maaza|kinley|smart|charged/i.test(b);

function StateChart({ state, data, metric, colors, maxVal }) {
  const fmt = metric === "vpo" ? fmtVpo : fmtWd;
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`,
      borderRadius: 8, padding: "10px 8px 4px" }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: T.text,
        paddingLeft: 6, marginBottom: 2, display: "flex", justifyContent: "space-between" }}>
        <span>{state}</span>
        <span style={{ fontSize: 9, fontWeight: 600, color: T.textDim, paddingRight: 6 }}>
          {metric === "vpo" ? "VPO" : "WD%"}
        </span>
      </div>
      <ResponsiveContainer width="100%" height={168}>
        <BarChart data={data} margin={{ top: 18, right: 6, bottom: 34, left: 38 }}
          barCategoryGap="14%">
          <CartesianGrid strokeDasharray="2 4" stroke={T.border} vertical={false} />
          <XAxis dataKey="label" tick={{ fill: T.textMid, fontSize: 7.5 }}
            axisLine={{ stroke: "#CFCFCF" }} tickLine={false}
            angle={-35} textAnchor="end" interval={0} height={34} />
          {/* Shared scale across states so the small multiples compare honestly */}
          <YAxis hide domain={[0, maxVal]} />
          <Tooltip cursor={{ fill: `${T.accent}08` }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const d = payload[0].payload;
              return (
                <div style={{ background: "rgba(255,255,255,0.97)",
                  border: `1px solid ${T.borderMed}`, borderRadius: 8,
                  padding: "8px 12px", fontSize: 11, boxShadow: T.shadow }}>
                  <div style={{ fontWeight: 700 }}>{d.label}</div>
                  <div style={{ color: T.textDim, fontSize: 9.5, marginBottom: 4 }}>{state}</div>
                  <div style={{ color: T.textMid }}>WD%: <b style={{ color: T.text }}>{fmtWd(d.wd) || "—"}</b></div>
                  <div style={{ color: T.textMid }}>VPO: <b style={{ color: T.text }}>{fmtVpo(d.vpo) || "—"}</b></div>
                </div>
              );
            }} />
          <ReferenceLine y={0} stroke="#CFCFCF" />
          <Bar dataKey={metric} radius={[2, 2, 0, 0]}>
            {data.map((d, i) => <Cell key={i} fill={colors[d.key] || "#B3B3B3"} />)}
            <LabelList dataKey={metric} position="top" formatter={fmt}
              style={{ fill: T.textMid, fontSize: 8, fontWeight: 600 }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/** embedded — drops the headline strip and page padding so the blocks sit
 *  inside another page as ordinary cards. Returns null on no data there,
 *  rather than putting an error panel mid-page. */
export default function BrandView({ data, debug = [], onAskAI, embedded = false }) {
  if (!data) {
    if (embedded) return null;
    return (
      <div style={{ padding: 24 }}>
        <div style={{ background: T.surface, border: `1px solid ${T.border}`,
          borderRadius: 10, padding: 20, boxShadow: T.shadow }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>No 10_brand sheet could be read</div>
          <div style={{ fontSize: 11.5, color: T.textMid, marginTop: 6 }}>
            Expected a pivot with <b>IN SUB-BRAND</b> / <b>IN PACK SIZE</b> down the left and
            a <b>Sum of WD% / Sum of VPO</b> column pair per state.
          </div>
          {debug.length > 0 && (
            <div style={{ marginTop: 12, fontSize: 10.5, fontFamily: "monospace", color: T.textMid }}>
              {debug.map((d, i) => (
                <div key={i}><b>{d.sheet}</b> — <span style={{ color: T.neg }}>{d.why}</span></div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  const { states, byKey, series } = data;

  const defaults = useMemo(() => defaultSeriesKeys(series), [series]);
  const [picked, setPicked] = useState(() => defaults.map(d => d.key));
  const [stateSel, setStateSel] = useState(() => {
    const d = DEFAULT_BRAND_STATES.filter(s => states.includes(s)).slice(0, MAX_STATES);
    return d.length ? d : states.slice(0, MAX_STATES);
  });

  const colors = useMemo(() => {
    const m = {};
    defaults.forEach(d => { m[d.key] = d.color; });
    series.forEach((s, i) => { if (!m[s.key]) m[s.key] = brandFamilyColor(s.brand, i); });
    return m;
  }, [defaults, series]);

  /* One row per picked series, in the deck's order */
  const seriesFor = (state) => picked
    .map(k => {
      const row = byKey[k];
      if (!row) return null;
      const v = row.values[state] || {};
      return { key: k, label: row.label, wd: v.wd, vpo: v.vpo };
    })
    .filter(Boolean);

  const shownStates = stateSel.filter(s => states.includes(s));

  /* A shared y-max per metric keeps the small multiples comparable */
  const maxes = useMemo(() => {
    let vpo = 0, wd = 0;
    shownStates.forEach(st => seriesFor(st).forEach(d => {
      if (d.vpo != null) vpo = Math.max(vpo, d.vpo);
      if (d.wd != null) wd = Math.max(wd, d.wd);
    }));
    return { vpo: vpo * 1.25 || 1, wd: wd * 1.25 || 1 };
  }, [shownStates, picked, byKey]);

  // Capped at MAX_STATES — past six the side-by-side rows get too long to compare
  const toggleState = (s) =>
    setStateSel(v => (v.includes(s)
      ? v.filter(x => x !== s)
      : v.length >= MAX_STATES ? v : [...v, s]));
  const toggleSeries = (k) =>
    setPicked(v => (v.includes(k) ? v.filter(x => x !== k) : [...v, k]));

  /* One row per state: VPO on the left, WD% on the right, so the two
     measures for a state sit next to each other. Each column keeps a shared
     scale across states so the rows compare honestly. */
  const SideBySide = () => (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`,
      borderRadius: 10, padding: 16, boxShadow: T.shadow }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Volume per Outlet (VPO)</div>
          <div style={{ fontSize: 10, color: T.textDim }}>shared scale across states</div>
        </div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Weighted Distribution (WD%)</div>
          <div style={{ background: "#EDEDED", color: T.text, fontSize: 9.5, fontWeight: 700,
            display: "inline-block", padding: "2px 8px", borderRadius: 4, marginTop: 2 }}>
            OUR REACH COMPARED TO PLAYERS@10 NEEDS SCALE
          </div>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {shownStates.map(st => (
          <div key={st} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <StateChart state={st} data={seriesFor(st)} metric="vpo"
              colors={colors} maxVal={maxes.vpo} />
            <StateChart state={st} data={seriesFor(st)} metric="wd"
              colors={colors} maxVal={maxes.wd} />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14,
      padding: embedded ? 0 : 16 }}>
      {!embedded && (
      <div style={{ background: T.ink, borderRadius: 10, padding: "14px 20px", color: "#fff",
        display: "flex", flexWrap: "wrap", alignItems: "center", gap: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>
          Rs 10 — Zero Sugar variants vs Campa: volume per outlet and reach
        </div>
        {onAskAI && (
          <button onClick={onAskAI} style={{ marginLeft: "auto", background: T.accent,
            border: "none", color: "#fff", padding: "6px 12px", borderRadius: 8,
            fontSize: 11, fontWeight: 700, cursor: "pointer" }}>🤖 Ask AI</button>
        )}
      </div>
      )}

      {/* Pack legend / picker */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`,
        borderRadius: 10, padding: "10px 14px", boxShadow: T.shadow }}>
        <div style={{ fontSize: 9.5, letterSpacing: 1, textTransform: "uppercase",
          fontWeight: 700, color: T.textDim, marginBottom: 6 }}>Packs</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {series.map(s => {
            const on = picked.includes(s.key);
            return (
              <button key={s.key} onClick={() => toggleSeries(s.key)} style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "3px 9px", borderRadius: 999, cursor: "pointer",
                border: `1px solid ${on ? T.accent : T.border}`,
                background: on ? T.accentSoft : "transparent",
                color: on ? T.text : T.textDim, fontSize: 10.5,
                fontWeight: on ? 700 : 500,
              }}>
                <span style={{ width: 9, height: 9, borderRadius: 2,
                  background: colors[s.key] || "#B3B3B3",
                  border: "1px solid rgba(0,0,0,0.12)" }} />
                {s.label}
                {isKO(s.brand) && <span style={{ fontSize: 8, color: T.pos }}>KO</span>}
              </button>
            );
          })}
        </div>

        <div style={{ fontSize: 9.5, letterSpacing: 1, textTransform: "uppercase",
          fontWeight: 700, color: T.textDim, margin: "10px 0 6px" }}>States</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
          {states.map(s => {
            const on = stateSel.includes(s);
            const full = !on && stateSel.length >= MAX_STATES;
            return (
              <button key={s} onClick={() => toggleState(s)} disabled={full}
                title={full ? `Up to ${MAX_STATES} states — deselect one first` : ""}
                style={{
                  padding: "2px 9px", borderRadius: 4, cursor: full ? "not-allowed" : "pointer",
                  border: `1px solid ${on ? `${T.accent}70` : T.border}`,
                  background: on ? `${T.accent}14` : "transparent",
                  color: on ? T.text : T.textDim, fontSize: 10,
                  fontWeight: on ? 600 : 400, opacity: full ? 0.4 : 1,
                }}>{s}</button>
            );
          })}
          <span style={{ fontSize: 10, color: T.textDim, alignSelf: "center", marginLeft: 4 }}>
            {stateSel.length}/{MAX_STATES}
          </span>
          <button onClick={() => setStateSel(
            DEFAULT_BRAND_STATES.filter(s => states.includes(s)).slice(0, MAX_STATES))}
            style={linkBtn}>Reset</button>
        </div>
      </div>

      <SideBySide />

      <div style={{ fontSize: 9.5, color: T.textDim, fontStyle: "italic",
        paddingBottom: embedded ? 4 : 0 }}>
        source sheet "{data.sheetName}" · {states.length} states · {series.length} brand-pack combinations
      </div>
    </div>
  );
}

const linkBtn = {
  padding: "2px 8px", borderRadius: 4, cursor: "pointer", border: "none",
  background: "none", color: T.accent, fontSize: 10, fontWeight: 700,
};